// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Mandatory Google-link step. Runs right after the custom-token sign-in (VerifyScreen) and
// before phone enrollment. The patient links a Google credential onto the qh-patients
// Firebase user (linkWithRedirect), so a future native app can Google-login directly. Google
// uses a full-page redirect, so on app return we complete getRedirectResult behind a loader
// gate (no screen flash), then call POST /v1/patient/identity/bind with the Firebase ID token
// to bind the identity to the deeplink-known patient record and refresh the session JWT. Once
// bound, we advance to /phone (which requires the bound session).

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  GoogleAuthProvider,
  linkWithRedirect,
  getRedirectResult,
  type AuthProvider as FirebaseAuthProvider,
} from 'firebase/auth'
import { ShieldCheck, Loader2, AlertCircle } from 'lucide-react'
import { OnboardingLayout } from '@/components/OnboardingLayout'
import { Logo } from '@/components/Logo'
import { LanguageSelector } from '@/i18n/LanguageSelector'
import { auth } from '@/lib/firebase'
import { bindIdentity, ApiError } from '@/lib/api'
import { setSession, clearSession } from '@/lib/session'

// Provider registry — adding Microsoft later is a one-liner: uncomment the entry.
// Only Google is wired for the pilot (mandatory link).
interface LinkProvider {
  id: string
  labelKey: string
  make: () => FirebaseAuthProvider
  Icon: () => React.ReactElement
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  )
}

const PROVIDERS: LinkProvider[] = [
  { id: 'google', labelKey: 'auth.linkGoogleButton', make: () => new GoogleAuthProvider(), Icon: GoogleIcon },
  // Microsoft (add when needed): import OAuthProvider from 'firebase/auth' and add:
  // { id: 'microsoft', labelKey: 'auth.linkMicrosoftButton', make: () => new OAuthProvider('microsoft.com'), Icon: MicrosoftIcon },
]

// Maps a bind ApiError to an i18n key; 401 is handled separately (route back to /verify).
function bindErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      if (err.code === 'google_required') return 'auth.errGoogleRequired'
      if (err.code === 'wrong_tenant') return 'auth.errWrongTenant'
    }
    if (err.status === 502 || err.code === 'verify_failed') return 'auth.errVerifyFailed'
  }
  return 'auth.errGeneric'
}

// Maps a Firebase linkWithRedirect/getRedirectResult error to an i18n key. Shared by both
// call sites so a network hiccup or an already-linked account reads the same either way.
function googleLinkErrorKey(err: unknown): string {
  const code =
    typeof err === 'object' && err !== null && 'code' in err ? (err as { code: string }).code : ''
  switch (code) {
    case 'auth/credential-already-in-use':
      return 'auth.errAlreadyInUse'
    case 'auth/network-request-failed':
      return 'auth.errNetwork'
    case 'auth/provider-already-linked':
      // The current Firebase user already has Google linked (e.g. browser-back after
      // consent). Not a failure the patient caused — surface the generic neutral copy.
      return 'auth.errGeneric'
    default:
      return 'auth.errGeneric'
  }
}

type Phase = 'settling' | 'prompt' | 'linking' | 'binding' | 'error'

export function LinkGoogleScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [phase, setPhase] = useState<Phase>('settling')
  const [errorKey, setErrorKey] = useState<string | null>(null)
  // True when the failure was in the bind step (not the Google link). Retry must re-run
  // completeBind — re-running the link would hit provider-already-linked and never recover.
  const [canRetryBind, setCanRetryBind] = useState(false)
  const ranRef = useRef(false)

  // On mount, settle any returning redirect BEHIND the loader (never flash the prompt).
  // getRedirectResult yields the linked credential on return, or null on a fresh visit.
  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true

    getRedirectResult(auth)
      .then(async (cred) => {
        if (cred) {
          await completeBind()
          return
        }
        setPhase('prompt')
      })
      .catch((err: unknown) => {
        setCanRetryBind(false)
        setErrorKey(googleLinkErrorKey(err))
        setPhase('error')
      })
  }, [])

  async function completeBind(): Promise<void> {
    setPhase('binding')
    setErrorKey(null)
    const user = auth.currentUser
    if (!user) {
      // No Firebase user (e.g. direct nav or lost session) — restart identity.
      navigate('/verify')
      return
    }
    try {
      // Force-refresh so the just-linked Google claim is present in the token the backend
      // verifies (a cached token can predate the link).
      const idToken = await user.getIdToken(true)
      const res = await bindIdentity(idToken)
      setSession(res.sessionJwt)
      // Identity is now bound (session carries patient_user_id). Enroll the phone credential
      // next — it 409s identity_not_bound without a bound session, so it must follow bind.
      navigate('/phone')
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Session died server-side (expired/consumed). Re-verifying a spent token dead-ends,
        // so end the session and route to the honest "request a new link" terminal.
        clearSession()
        navigate('/link-expired')
        return
      }
      // Bind failed but Google is already linked — retry must re-run the bind, not the link.
      // Exception: google_required means the link didn't take, so keep the link buttons
      // (a bind-retry could never satisfy it) rather than trapping the patient on retry.
      const needsRelink = err instanceof ApiError && err.status === 403 && err.code === 'google_required'
      setCanRetryBind(!needsRelink)
      setErrorKey(bindErrorKey(err))
      setPhase('error')
    }
  }

  async function handleLink(provider: LinkProvider): Promise<void> {
    const user = auth.currentUser
    if (!user) {
      navigate('/verify')
      return
    }
    setErrorKey(null)
    setPhase('linking')
    try {
      // Full-page redirect — navigates away; the result is picked up on return above.
      await linkWithRedirect(user, provider.make())
    } catch (err) {
      setCanRetryBind(false)
      setErrorKey(googleLinkErrorKey(err))
      setPhase('error')
    }
  }

  if (phase === 'settling' || phase === 'linking' || phase === 'binding') {
    const noteKey =
      phase === 'binding' ? 'auth.bindingNote' : phase === 'linking' ? 'auth.linkingNote' : 'auth.settlingNote'
    return (
      <OnboardingLayout>
        <div className="flex flex-col flex-1 items-center justify-center px-6 py-16 gap-6 text-center">
          <div className="h-16 w-16 rounded-2xl bg-card border border-border flex items-center justify-center shadow-sm">
            <Loader2 className="h-7 w-7 text-primary animate-spin" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">{t('auth.connectingTitle')}</h2>
            <p className="text-muted-foreground text-sm mt-2 leading-relaxed max-w-xs mx-auto">{t(noteKey)}</p>
          </div>
        </div>
      </OnboardingLayout>
    )
  }

  const content = (
    <div className="flex flex-col gap-4">
      {phase === 'error' && errorKey && (
        <div role="alert" className="flex items-start gap-3 px-4 py-3 rounded-xl bg-destructive/8 border border-destructive/20">
          <AlertCircle className="h-4 w-4 shrink-0 text-destructive mt-0.5" />
          <p className="text-sm text-destructive leading-snug">{t(errorKey)}</p>
        </div>
      )}

      {canRetryBind ? (
        // Google is already linked; only the bind failed — retry the bind, not the link.
        <button
          onClick={() => void completeBind()}
          className="w-full h-11 rounded-xl font-semibold text-sm text-white flex items-center justify-center bg-gradient-to-r from-[#006EF9] to-[#02C7C3] shadow-lg shadow-primary/20 hover:opacity-90 active:opacity-80 transition-opacity"
        >
          {t('auth.retryButton')}
        </button>
      ) : (
        <div className="flex flex-col gap-3">
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              onClick={() => void handleLink(p)}
              className="w-full flex items-center gap-4 bg-card border border-border rounded-2xl px-5 py-4 hover:border-primary/30 hover:bg-secondary/40 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0 border border-border">
                <p.Icon />
              </div>
              <p className="flex-1 font-semibold text-foreground text-[14px]">{t(p.labelKey)}</p>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <ShieldCheck className="w-4 h-4 text-[#02C7C3] flex-shrink-0" />
        <p className="text-[12px] text-muted-foreground">{t('auth.hipaaNote')}</p>
      </div>
      <p className="text-[12px] text-muted-foreground leading-relaxed">{t('auth.linkNote')}</p>
    </div>
  )

  return (
    <OnboardingLayout>
      {/* Mobile */}
      <div className="flex flex-col flex-1 md:hidden">
        <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9] flex-shrink-0" />
        <div className="flex-1 px-5 pt-10 pb-6 overflow-y-auto">
          <div className="flex justify-end mb-6">
            <LanguageSelector />
          </div>
          <h1 className="text-[24px] font-bold text-foreground leading-tight mb-1">{t('auth.linkTitle')}</h1>
          <p className="text-muted-foreground text-[14px] leading-relaxed mb-8">{t('auth.linkSubtitle')}</p>
          {content}
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden md:flex flex-col flex-1">
        <header className="px-10 py-5 flex items-center justify-between border-b border-border bg-card">
          <Logo variant="wordmark" className="h-8 w-auto" />
          <LanguageSelector />
        </header>
        <div className="flex flex-1 items-center justify-center px-6 py-12">
          <div className="w-full max-w-md bg-card rounded-3xl shadow-sm border border-border overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9]" />
            <div className="p-10">
              <h1 className="text-2xl font-bold text-foreground mb-1">{t('auth.linkTitle')}</h1>
              <p className="text-muted-foreground text-[14px] leading-relaxed mb-8">{t('auth.linkSubtitle')}</p>
              {content}
            </div>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  )
}
